/**
 * author-sphere.ts — sfera 3D condivisa per i nodi-autore (Mappa dei temi e
 * Rete delle influenze).
 *
 * LOGICA COLORE = PERIODO letterario dell'autore: ogni epoca ha la sua tinta,
 * così le sfere non sono più tutte uguali e l'epoca si legge a colpo d'occhio
 * (e si accorda con le fasce dei secoli della timeline). Il bordeaux brand
 * resta riservato a selezione/evidenziazione (anelli), non al riempimento.
 *
 * PROFONDITÀ 3D (marcata): gradiente radiale con highlight caldo in alto-sx,
 * terminatore molto scuro in basso-dx, riflesso speculare a due livelli e una
 * "luce di rimbalzo" sul bordo basso (il dettaglio che dà la rotondità) + ombra
 * portata profonda. Helper esportati così entrambe le mappe rendono identico.
 */

/** Tinta per periodo (mid-tone; il gradiente schiarisce/scurisce da qui). */
const PERIOD_COLOR: Record<string, string> = {
  'old-english-medieval': '#5f6b78', // peltro/ardesia
  renaissance:            '#b0863a', // oro
  'restoration-18c':      '#7e6a9c', // ametista
  /* 🐞 TRE TINTE SPOSTATE — 06/09/2026. Stavano nella BANDA MORTA: troppo
     chiare perche' il bianco arrivi a 4,5:1, troppo scure perche' ci arrivi
     l'inchiostro scuro. Nessuno dei due inchiostri le reggeva, quindi non
     bastava la regola di `siglaInk` qui sotto: andava spostata la tinta.
     Scurite del minimo indispensabile, tonalita' e saturazione invariate:
       bronzo      #917f5c → #847454   bianco 3,89 → 4,56
       terracotta  #b26a47 → #a96543   bianco 4,16 → 4,54
       oliva       #6f7d45 → #6e7c44   bianco 4,47 → 4,54
     Le altre sette non si toccano: il RINASCIMENTO (#b0863a) col bianco fa
     3,32 ma con lo scuro 4,66, e `siglaInk` lo sceglie da solo. */
  colonial:               '#847454', // bronzo
  romantic:               '#a2454f', // rosa-bordeaux
  'american-romanticism': '#a96543', // terracotta
  victorian:              '#3f7d6a', // verde viridian
  'american-realism':     '#6e7c44', // oliva
  modernist:              '#46688f', // blu acciaio
  contemporary:           '#6a6a72', // grafite
};
const FALLBACK = '#6f5a60';

/** Colore-sfera di un autore dal suo primo periodo (fallback neutro). */
export function authorSphereColor(periods: readonly string[] | undefined): string {
  const p = periods && periods[0];
  return (p && PERIOD_COLOR[p]) || FALLBACK;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix(a: [number, number, number], b: [number, number, number], t: number): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}
const lighten = (hex: string, t: number) => mix(hexToRgb(hex), [255, 255, 255], t);
const darken = (hex: string, t: number) => mix(hexToRgb(hex), [0, 0, 0], t);

/**
 * L'INCHIOSTRO DELLA SIGLA SI SCEGLIE, NON SI ASSUME — 06/09/2026.
 *
 * Standard EduTool, `regole.json` § sigla. La sigla era `fill="#fff"` cablato,
 * e su queste dieci tinte d'epoca il bianco NON vince sempre: sul RINASCIMENTO
 * (#b0863a, oro) fa 3,32:1 — sotto AA — mentre l'inchiostro scuro fa 4,66.
 * Con la scelta, quella sigla passa senza toccare la tinta.
 *
 * Tre righe, ereditabili da tutte e sei le repo. Sulle 51 tinte della famiglia
 * il bianco cablato ne lasciava fallire 44, col caso peggiore a 2,20:1.
 *
 * ⚠️ La seconda meta' della regola non e' codice ma vincolo sulla tavolozza:
 * una tinta deve reggere ALMENO UNO dei due inchiostri a 4,5:1. Lo controlla
 * `controlla.py`, controllo `sigla-illeggibile`.
 */
export function siglaInk(hex: string): string {
  const lin = (c: number) => (c / 255 <= 0.03928 ? c / 255 / 12.92 : ((c / 255 + 0.055) / 1.055) ** 2.4);
  const [r, g, b] = hexToRgb(hex);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  const suBianco = 1.05 / (L + 0.05);
  const suScuro = (L + 0.05) / (LUMINANZA_INK_SCURO + 0.05);
  return suBianco >= suScuro ? '#ffffff' : INK_SCURO;
}
/** L'inchiostro scuro della tavolozza, non un nero nuovo. */
const INK_SCURO = '#262420';
const LUMINANZA_INK_SCURO = 0.01778;

/** id stabile di gradiente da un colore (per i <defs>). */
export function sphereGradId(hex: string): string { return 'asph-' + hex.replace('#', ''); }

/** Stop del gradiente sfera per una tinta (riuso DOM + stringa). */
export function sphereStops(hex: string): Array<{ offset: string; color: string }> {
  return [
    { offset: '0%', color: lighten(hex, 0.74) },
    { offset: '15%', color: lighten(hex, 0.36) },
    { offset: '54%', color: hex },
    { offset: '100%', color: darken(hex, 0.56) },
  ];
}
/** Colore della luce di rimbalzo sul bordo basso. */
export const sphereRim = (hex: string) => lighten(hex, 0.62);

/** <defs> con un gradiente per ogni tinta usata + l'ombra condivisa (id auth-sh). */
export function authorSphereDefs(colors: string[]): string {
  const uniq = [...new Set(colors)];
  const grads = uniq.map((c) => `<radialGradient id="${sphereGradId(c)}" cx="33%" cy="25%" r="84%">
      ${sphereStops(c).map((s) => `<stop offset="${s.offset}" stop-color="${s.color}"/>`).join('')}
    </radialGradient>`).join('');
  const filt = `<filter id="auth-sh" x="-70%" y="-70%" width="240%" height="240%">
      <feDropShadow dx="0" dy="3.5" stdDeviation="4" flood-color="#120d0b" flood-opacity="0.40"/>
    </filter>`;
  return grads + filt;
}

/** Sfera autore (stringa SVG) con la tinta `baseHex` (vedi authorSphereDefs). */
export function authorSphere(cx: number, cy: number, r: number, ini: string, fp: number, baseHex: string): string {
  const rim = sphereRim(baseHex);
  const safe = ini.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
  return `<g>
    <circle cx="${cx}" cy="${cy}" r="${r}" fill="url(#${sphereGradId(baseHex)})" filter="url(#auth-sh)"/>
    <ellipse cx="${cx - r * 0.30}" cy="${cy - r * 0.36}" rx="${r * 0.48}" ry="${r * 0.32}" fill="rgba(255,255,255,0.50)" pointer-events="none"/>
    <ellipse cx="${cx - r * 0.34}" cy="${cy - r * 0.42}" rx="${r * 0.18}" ry="${r * 0.12}" fill="rgba(255,255,255,0.92)" pointer-events="none"/>
    <path d="M ${cx - r * 0.66} ${cy + r * 0.48} A ${r * 0.82} ${r * 0.82} 0 0 0 ${cx + r * 0.66} ${cy + r * 0.48}"
          fill="none" stroke="${rim}" stroke-opacity="0.66" stroke-width="${r * 0.13}" stroke-linecap="round" pointer-events="none"/>
    <text x="${cx}" y="${cy + fp * 0.34}" text-anchor="middle" font-family="Fraunces, serif" font-weight="700" font-size="${fp}" fill="${siglaInk(baseHex)}" pointer-events="none">${safe}</text>
  </g>`;
}
